import { OutlinedInput, Stack, Typography } from '@mui/material';
import { FormField } from '../form/FormField';
import { graphql } from '../gql';
import { useModalContext } from '../modal/ModalContext';
import { ModalActions } from '../modal/components/ModalActions';
import { ModalContent } from '../modal/components/ModalContent';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { FC } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { z } from 'zod';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';
import { useSnackbar } from '../store/snackbar';

const journey_DeleteJourney = graphql(`
	mutation journey_DeleteJourney($id: UUID!) {
		deleteJourney(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

const getJourney = graphql(`
	query journey_getJourney($id: UUID!) {
		getJourney(by: { id: $id }) {
			phases {
				id
			}
		}
	}
`);

const useDeleteJourney = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { mutate } = useApiMutation(journey_DeleteJourney, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.deleteJourney.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'journey.success.delete' }),
					error: intl.formatMessage({ id: 'journey.error.delete' }),
				},
				errorMessage: result.deleteJourney.errorMessage,
				openSnackbar,
			}),
	});

	return { mutate };
};

export const deleteJourneyModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const createFormSchema = (textToMatch: string, intl: IntlShape) =>
	z.object({
		confirmField: z.literal(textToMatch, {
			errorMap: () => ({ message: intl.formatMessage({ id: 'common.delete.invalid' }, { desired: textToMatch }) }),
		}),
	});

export const DeleteJourneyModal: FC<z.infer<typeof deleteJourneyModalSchema>> = ({ id, name }) => {
	const intl = useIntl();
	const { mutate: deleteJourney } = useDeleteJourney();
	const { data } = useApiQuery(getJourney, { variables: { id } });
	const { closeModal } = useModalContext();
	const form = useTypedForm({
		schema: createFormSchema(name, intl),
	});

	const requiresConfirmation = data?.getJourney ? data.getJourney.phases.length > 0 : false;

	return (
		<>
			<ModalContent
				isDelete
				requiresConfirmation={requiresConfirmation}
				title={intl.formatMessage({ id: 'journey.delete-modal.title' })}
				description={intl.formatMessage({ id: 'journey.delete-modal.description' }, { name })}
			>
				<>
					<Typography textAlign={'center'}>
						<strong>
							<FormattedMessage id={'common.delete.confirmation'} />
						</strong>
					</Typography>
					<Stack width={'50%'} alignSelf={'center'}>
						<form
							noValidate
							onSubmit={(e) => {
								e.preventDefault();
								if (form.formState.isValid) {
									deleteJourney({ id });
									closeModal();
								}
							}}
						>
							<FormField
								form={form}
								name={'confirmField'}
								label={''}
								render={(props) => (
									<OutlinedInput {...props} placeholder={intl.formatMessage({ id: 'common.delete.confirmation-placeholder' }, { name })} />
								)}
							/>
						</form>
					</Stack>
				</>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteJourney({ id });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
				disablePrimaryButton={requiresConfirmation ? !form.formState.isValid : false}
			/>
		</>
	);
};
